import type { ColumnDef, ColumnSizingState } from '@tanstack/react-table'
import type { Quote } from '@/types'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { formatDate, formatCurrency } from '@/lib/utils'

interface QuotesColumnsProps {
  onViewQuote: (quoteId: string, name: string) => void
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: () => void
  allSelected?: boolean
}

export const createQuotesColumns = ({
  onViewQuote,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
}: QuotesColumnsProps): ColumnDef<Quote>[] => [
  {
    id: 'select',
    header: () => (
      <Checkbox
        checked={allSelected}
        onCheckedChange={onToggleSelectAll}
        aria-label="Select all quotes"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={selectedIds?.has(row.original.id) ?? false}
        onCheckedChange={() => onToggleSelect?.(row.original.id)}
        aria-label={`Select quote ${row.original.name}`}
      />
    ),
    size: 50,
    minSize: 50,
    maxSize: 50,
    enableResizing: false,
  },
  {
    id: 'name',
    header: 'Quote Name',
    accessorKey: 'name',
    meta: { sortField: 'name' },
    size: 250,
    cell: ({ row }) =>
      row.original.id && row.original.name ? (
        <Link
          to={`/quotes/${row.original.id}`}
          className="font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label={`View quote: ${row.original.name}`}
        >
          {row.original.name}
        </Link>
      ) : (
        <span className="font-medium">{row.original.name || '-'}</span>
      ),
  },
  {
    id: 'opportunity_name',
    header: 'Opportunity Name',
    accessorKey: 'opportunity_name',
    meta: { sortField: 'opportunity_name' },
    size: 250,
    cell: ({ row }) =>
      row.original.opportunity_id && row.original.opportunity_name ? (
        <Link
          to={`/opportunities/${row.original.opportunity_id}`}
          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label={`View opportunity: ${row.original.opportunity_name}`}
        >
          {row.original.opportunity_name}
        </Link>
      ) : (
        <span>{row.original.opportunity_name || '-'}</span>
      ),
  },
  {
    id: 'expiration_date',
    header: 'Expiration',
    accessorKey: 'expiration_date',
    meta: { sortField: 'expiration_date' },
    size: 150,
    cell: ({ row }) =>
      row.original.expiration_date ? formatDate(row.original.expiration_date) : '-',
  },
  {
    id: 'total_price',
    header: 'Total',
    accessorKey: 'total_price',
    meta: { sortField: 'total_price' },
    size: 150,
    cell: ({ row }) =>
      row.original.total_price
        ? formatCurrency(row.original.total_price, row.original.currency_iso_code || 'CAD')
        : '-',
  },
  {
    id: 'owner_name',
    header: 'Owner',
    accessorKey: 'owner_name',
    meta: { sortField: 'owner_name' },
    size: 150,
    cell: ({ row }) => (
      <span className="truncate block max-w-[150px]">
        {(row.original as any).owner_name || '-'}
      </span>
    ),
  },
  {
    id: 'actions',
    header: () => <div className="text-right">Actions</div>,
    size: 100,
    minSize: 80,
    maxSize: 150,
    enableResizing: false,
    cell: ({ row }) => (
      <div className="text-right">
        <Button
          variant="ghost"
          size="sm"
          aria-label={`View quote ${row.original.name}`}
          onClick={() => onViewQuote(row.original.id, row.original.name)}
        >
          View
        </Button>
      </div>
    ),
  },
]

export const quotesDefaultColumnSizing: ColumnSizingState = {
  select: 50,
  name: 250,
  opportunity_name: 250,
  expiration_date: 150,
  total_price: 150,
  owner_name: 150,
  actions: 100,
}
